// src/hooks/useBufferAudioSync.ts
import { useCallback, useEffect, useRef, useState } from "react";

type Status = "idle" | "loading" | "ready" | "playing" | "paused" | "error";

interface Options {
  stuUrl: string;
  refUrl?: string;
  refOffsetMs?: number;
  playbackSpeed?: number;
  outputGain?: number;
}

export function useBufferAudioSync({
  stuUrl,
  refUrl,
  refOffsetMs = 0,
  playbackSpeed = 1.0,
  outputGain = 1.0,
}: Options) {
  const [status, setStatus] = useState<Status>("idle");
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);

  const ctxRef = useRef<AudioContext | null>(null);

  const stuBufRef = useRef<AudioBuffer | null>(null);
  const refBufRef = useRef<AudioBuffer | null>(null);

  const outputGainRef = useRef<GainNode | null>(null);
  const stuGainRef = useRef<GainNode | null>(null);
  const refGainRef = useRef<GainNode | null>(null);

  const stuSrcRef = useRef<AudioBufferSourceNode | null>(null);
  const refSrcRef = useRef<AudioBufferSourceNode | null>(null);

  // Horloge AUDIO unique
  const startCtxTimeRef = useRef<number | null>(null);
  const offsetAtStartRef = useRef<number>(0);

  // RAF ticker (UI truth)
  const rafRef = useRef<number | null>(null);

  const ensureCtx = useCallback(() => {
    if (!ctxRef.current) {
      const Ctx =
        (window.AudioContext ||
          (window as any).webkitAudioContext) as typeof AudioContext;
      const ctx = new Ctx();
      ctxRef.current = ctx;

      outputGainRef.current = ctx.createGain();
      outputGainRef.current.gain.value = outputGain;

      stuGainRef.current = ctx.createGain();
      refGainRef.current = ctx.createGain();

      stuGainRef.current.connect(outputGainRef.current);
      refGainRef.current.connect(outputGainRef.current);
      outputGainRef.current.connect(ctx.destination);
    }
    return ctxRef.current!;
  }, [outputGain]);

  const stopNodes = useCallback(() => {
    try {
      stuSrcRef.current?.stop();
    } catch {}
    try {
      refSrcRef.current?.stop();
    } catch {}
    stuSrcRef.current = null;
    refSrcRef.current = null;
  }, []);

  const stopTicker = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
  }, []);

  const startTicker = useCallback(() => {
    stopTicker();

    const tick = () => {
      const ctx = ctxRef.current;
      const startCtxTime = startCtxTimeRef.current;

      if (!ctx || startCtxTime === null) {
        rafRef.current = null;
        return;
      }

      // Audio-truth time = (ctx.currentTime - startCtxTime) * speed + offsetAtStart
      const t =
        (ctx.currentTime - startCtxTime) * playbackSpeed +
        offsetAtStartRef.current;

      // Clamp to student buffer duration if known
      const dur = stuBufRef.current?.duration ?? Infinity;
      const safeT = Number.isFinite(dur) ? Math.max(0, Math.min(dur, t)) : Math.max(0, t);

      setCurrentTime(safeT);

      // If we reached end, stop cleanly (prevents infinite "playing" state)
      if (Number.isFinite(dur) && safeT >= dur - 0.002) {
        // emulate pause/end
        stopNodes();
        startCtxTimeRef.current = null;
        offsetAtStartRef.current = safeT;
        setIsPlaying(false);
        setStatus("paused");
        stopTicker();
        return;
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
  }, [playbackSpeed, stopNodes, stopTicker]);

  const loadBuffers = useCallback(async () => {
    if (!stuUrl) return;
    setStatus("loading");

    try {
      const ctx = ensureCtx();

      const fetchDecode = async (url: string) => {
        const res = await fetch(url);
        const ab = await res.arrayBuffer();
        return await ctx.decodeAudioData(ab.slice(0));
      };

      stuBufRef.current = await fetchDecode(stuUrl);
      refBufRef.current = refUrl ? await fetchDecode(refUrl) : null;

      setStatus("ready");
    } catch (e) {
      console.error("[useBufferAudioSync] loadBuffers error", e);
      setStatus("error");
    }
  }, [stuUrl, refUrl, ensureCtx]);

  useEffect(() => {
    stopTicker();
    stopNodes();
    setIsPlaying(false);
    setCurrentTime(0);
    startCtxTimeRef.current = null;
    offsetAtStartRef.current = 0;
    void loadBuffers();
  }, [stuUrl, refUrl, loadBuffers, stopNodes, stopTicker]);

  const startAt = useCallback(
    async (timeSec: number) => {
      const ctx = ensureCtx();
      if (ctx.state === "suspended") await ctx.resume();
      if (!stuBufRef.current) return;

      stopNodes();
      stopTicker();

      const startDelay = 0.05;
      const startCtxTime = ctx.currentTime + startDelay;

      const stu = ctx.createBufferSource();
      stu.buffer = stuBufRef.current;
      stu.playbackRate.value = playbackSpeed;
      stu.connect(stuGainRef.current!);

      // clamp start offset inside buffer
      const stuDur = stuBufRef.current.duration;
      const stuOffset = Math.max(0, Math.min(stuDur - 0.001, timeSec));
      stu.start(startCtxTime, stuOffset);
      stuSrcRef.current = stu;

      if (refUrl && refBufRef.current) {
        const ref = ctx.createBufferSource();
        ref.buffer = refBufRef.current;
        ref.playbackRate.value = playbackSpeed;
        ref.connect(refGainRef.current!);

        const refDur = refBufRef.current.duration;
        const refOffset = Math.max(
          0,
          Math.min(refDur - 0.001, timeSec + refOffsetMs / 1000)
        );

        ref.start(startCtxTime, refOffset);
        refSrcRef.current = ref;
      }

      startCtxTimeRef.current = startCtxTime;
      offsetAtStartRef.current = stuOffset;

      setIsPlaying(true);
      setStatus("playing");

      // ✅ start UI ticker (this fixes your video drift loop)
      startTicker();
    },
    [ensureCtx, stopNodes, playbackSpeed, refUrl, refOffsetMs, stopTicker, startTicker]
  );

  const play = useCallback(async () => {
    if (status === "loading") return;
    await startAt(currentTime);
  }, [startAt, currentTime, status]);

  const pause = useCallback(() => {
    const ctx = ctxRef.current;
    if (!ctx || startCtxTimeRef.current === null) return;

    const t =
      (ctx.currentTime - startCtxTimeRef.current) * playbackSpeed +
      offsetAtStartRef.current;

    stopTicker();
    stopNodes();
    startCtxTimeRef.current = null;
    offsetAtStartRef.current = t;

    setCurrentTime(t);
    setIsPlaying(false);
    setStatus("paused");
  }, [stopNodes, playbackSpeed, stopTicker]);

  const seek = useCallback(
    async (timeSec: number) => {
      const dur = stuBufRef.current?.duration ?? Infinity;
      const t = Number.isFinite(dur)
        ? Math.max(0, Math.min(dur - 0.001, timeSec))
        : Math.max(0, timeSec);

      setCurrentTime(t);
      offsetAtStartRef.current = t;

      if (isPlaying) {
        await startAt(t);
      }
    },
    [isPlaying, startAt]
  );

  const togglePlay = useCallback(async () => {
    if (isPlaying) pause();
    else await play();
  }, [isPlaying, pause, play]);

  const setMix = useCallback((mixRatio: number) => {
    const x = Math.max(0, Math.min(1, mixRatio));
    if (stuGainRef.current) stuGainRef.current.gain.value = x;
    if (refGainRef.current) refGainRef.current.gain.value = 1 - x;
  }, []);

  const setOutputGain = useCallback((g: number) => {
    if (outputGainRef.current) {
      outputGainRef.current.gain.value = Math.max(0, Math.min(1, g));
    }
  }, []);

  useEffect(() => {
    return () => {
      stopTicker();
      stopNodes();
      try {
        ctxRef.current?.close();
      } catch {}
      ctxRef.current = null;
    };
  }, [stopNodes, stopTicker]);

  return {
    status,
    isPlaying,
    currentTime,
    togglePlay,
    seek,
    setMix,
    setOutputGain,
  };
}